import React, { useEffect, useRef, useState } from "react";
import {
  BackButton,
  Box,
  ButtonStyled,
  ThemeLoader,
} from "../../../components/styled/Elements";
import Breadcrumbs from "../../../components/admin/Breadcrumbs/Breadcrumbs";
import {
  FileIcon3,
  PlaceholderImg,
  PlusIcon,
} from "../../../components/styled/AllImages";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ListTable from "../../../components/common/tables/ListTable";
import {
  departmentsHeadings,
  hqBranchesHeadings,
} from "../../../helpers/dummyConstants";
import { departmentColumns } from "../../../helpers/adminConstants/TableConstants";
import DeleteModal from "../../../components/admin/Modals/DeleteModal/DeleteModal";
import HistoryModal from "../../../components/admin/Modals/HistoryModal/HistoryModal";
import HQBranchesApi from "../../../services/api/hqBranchesApi";
import { toast } from "react-toastify";
import { getFormattedDate, getFormattedTime } from "../../../utils";
import { pdf } from "@react-pdf/renderer";
import { useUser } from "../../../context/userContext";
import { PdfDocumentTemplate } from "../hqBranches/components/PdfDocumentTemplate";
import { useDispatch } from "react-redux";
import {
  setBranchesDeps,
  setHqBranchesList,
} from "../../../store/slices/helperSlice";

const ASSET_URL = process.env.REACT_APP_ASSET_URL;

const Departments = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useUser();
  const dispatch = useDispatch();

  const searchParams = new URLSearchParams(location.search);
  const hqBranchId = searchParams.get("org-structure");

  const [showDelete, setShowDelete] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [departmentsList, setDepartmentsList] = useState([]);
  const [departmentHistory, setDepartmentHistory] = useState({});
  const [activePage, setActivePage] = useState(1);
  const [totalCount, setTotalCount] = useState(null);
  const [perPage, setPerPage] = useState(null);
  const [departmentId, setDepartmentId] = useState(null);
  const iframeRef = useRef(null);

  const onDelete = (row) => {
    setDepartmentId(row.id);
    setShowDelete(true);
  };

  const onHistory = (row) => {
    getDepartmentHistory(row.id);
    setShowHistory(true);
  };

  const handleView = (row) => {
    navigate(
      `/org-structure/departments/view-department?org-structure=${hqBranchId}&department=${row.id}`
    );
  };

  const handleEdit = (row) => {
    navigate(
      `/org-structure/departments/edit-department?org-structure=${hqBranchId}&department=${row.id}`
    );
  };

  const handleStatusToggle = async (row) => {
    const newObj = {
      _method: "PUT",
      active: parseInt(row.status) === 1 ? 0 : 1,
    };
    try {
      const response = await HQBranchesApi.changeDepartmentStatus(
        hqBranchId,
        row.id,
        newObj
      );
      if (response.code === 200) {
        toast.success(t("department_status_updated"));
        getDepartmentsList();
      }
      if (response.code === 204 || response.code === 100) {
        toast.error(response.message);
      }
    } catch (error) {}
  };

  const handleDelete = async () => {
    try {
      const response = await HQBranchesApi.deleteDepartment(
        hqBranchId,
        departmentId
      );
      if (response.code === 200) {
        toast.success(t("department_deleted"));
        setShowDelete(false);
        setDepartmentId(null);
        getDepartmentsList();
      }
      if (response.code === 400) {
        toast.error(response?.error);
      }
      if (response.code === 204 || response.code === 100) {
        toast.error(response.message);
      }
    } catch (error) {}
  };

  const getDepartmentHistory = async (departmentId) => {
    try {
      setHistoryLoading(true);
      const { data, code } = await HQBranchesApi.getDepartmentHistory(
        hqBranchId,
        departmentId
      );
      if (code === 200) {
        const newCreatedBy = {
          id: data.id,
          name: data.created_by_name,
          subLabel: data?.department && data?.branchHQ ? `${data?.department} (${data?.branchHQ})` : "",
          avatar: data.created_by_profile
            ? `${ASSET_URL}/${data.created_by_profile}`
            : PlaceholderImg,
          date: getFormattedDate(data.created_at),
          time: getFormattedTime(data.created_at),
        };
        const editHistory =
          data.update_history.length > 0 &&
          data.update_history.map((user) => ({
            id: user.id,
            name: user.update_by_name,
            subLabel: user?.update_by_detail
              ? `${
                  language === "en"
                    ? user?.update_by_detail?.department_selective?.name_en
                    : user?.update_by_detail?.department_selective?.name_ar
                } (${
                  language === "en"
                    ? user?.update_by_detail?.office_location_selective?.name_en
                    : user?.update_by_detail?.office_location_selective?.name_ar
                })`
              : "",
            avatar: user.update_by_profile
              ? `${ASSET_URL}/${user.update_by_profile}`
              : PlaceholderImg,
            date: getFormattedDate(user.updated_at),
            time: getFormattedTime(user.updated_at),
          }));
        setDepartmentHistory((prevData) => ({
          ...prevData,
          createdBy: newCreatedBy,
          editHistory: editHistory,
        }));
      }
    } catch (error) {
    } finally {
      setHistoryLoading(false);
    }
  };

  const getBranchesDepartments = async () => {
    try {
      const { data, code } = await HQBranchesApi.getBranchesDepartments();

      if (code === 200) {
        const branchesDepsArr = data?.map((item) => ({
          id: item?.id,
          name: item?.name,
          subLabel: item?.location?.name,
          location_id: item?.location_id,
        }));

        dispatch(setBranchesDeps(branchesDepsArr));
      }
    } catch (error) {}
  };

  const getHqBranchesWithoutPg = async () => {
    try {
      const { data, code } = await HQBranchesApi.getHqBranchesListWithoutPg();
      if (code === 200) {
        dispatch(setHqBranchesList(data));
      }
    } catch (error) {}
  };

  const getDepartmentsList = async () => {
    try {
      setLoading(true);
      const { data, code } = await HQBranchesApi.getDepartmentsList(hqBranchId);
      if (code === 200 && data?.list?.length > 0) {
        getBranchesDepartments();
        getHqBranchesWithoutPg();
        const listArr = data?.list.map((item) => ({
          ...item,
          status: item.active,
          city: language === "en" ? item?.city?.en_name : item?.city?.ar_name,
        }));
        setDepartmentsList(listArr);
        setTotalCount(data?.pagination?.total);
        setPerPage(data?.pagination?.per_page);
      } else {
        setDepartmentsList([]);
        setTotalCount(null);
        setPerPage(null);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = async () => {
    try {
      const { data, code } = await HQBranchesApi.getCompleteDepartmentsList(
        hqBranchId
      );

      if (code === 200) {
        const resData = data?.map((item) => ({
          ...item,
          no_of_users: item.no_of_users,
          // name: language === "ar" ? item.name_ar : item.name_en,
        }));

        const blob = await pdf(
          <PdfDocumentTemplate
            user={user}
            departmentsHeadings={departmentsHeadings}
            departmentsList={resData}
          />
        ).toBlob();

        const url = URL.createObjectURL(blob);

        const iframe = iframeRef.current;
        if (iframe) {
          iframe.src = url;
          iframe.onload = () => {
            iframe.contentWindow.print();
          };
        }
      }
    } catch (error) {}
  };

  (async () => {
    const { data, code } = await HQBranchesApi.getCompleteDepartmentsList(
      hqBranchId
    );

    if (code === 200) {
      const resData = data?.map((item) => ({
        ...item,
        no_of_users: item.no_of_users,
        type_en: item.location_type == 1 ? "Headquarter" : "Branch",
        type_ar: item.location_type == 1 ? "المقر الرئيسي" : "فرع",
      }));
    }
  })();

  useEffect(() => {
    getDepartmentsList();
  }, [language]);

  return (
    <Box>
      <Box className="content-head">
        <Box className="d-flex align-items-center flex-wrap flex-sm-nowrap gap-3 gap-md-5">
          <BackButton
            label={t("departments")}
            onClick={() => navigate("/org-structure")}
          />
          <Breadcrumbs />
        </Box>
        <Box className="d-flex align-items-center gap-2 head-item-two">
          <ButtonStyled
            onClick={handlePrint}
            label={t("print_as_pdf")}
            icon={FileIcon3}
            bg="var(--themeColor2)"
          />
          <iframe
            ref={iframeRef}
            style={{ display: "none" }}
            title="PDF Print Frame"
          />
          <ButtonStyled
            label={t("add_department")}
            icon={PlusIcon}
            onClick={() =>
              navigate(
                `/org-structure/departments/add-department?org-structure=${hqBranchId}`
              )
            }
          />
        </Box>
      </Box>
      <Box>
        {loading ? (
          <ThemeLoader />
        ) : (
          <ListTable
            options={["view", "info", "edit", "status", "delete"]}
            rows={departmentsList}
            columns={departmentColumns}
            onDelete={onDelete}
            onInfoView={onHistory}
            onViewDetails={handleView}
            onEdit={handleEdit}
            activePage={activePage}
            setActivePage={setActivePage}
            onStatusToggle={handleStatusToggle}
            perPage={perPage}
            totalCount={totalCount}
          />
        )}
      </Box>

      <HistoryModal
        title={t("departments_history")}
        show={showHistory}
        onHide={() => setShowHistory(false)}
        historyLoading={historyLoading}
        createdBy={departmentHistory?.createdBy}
        editHistory={departmentHistory?.editHistory}
      />
      <DeleteModal
        show={showDelete}
        onHide={() => setShowDelete(false)}
        title={t("department")}
        onDelete={handleDelete}
      />
    </Box>
  );
};

export default Departments;
